import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    Box,
    Button,
    Alert
} from "@mui/material";
import { useState, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { CotacaoRealizada, DetalheSolicitacaoData } from "..";
import Step01 from "./Step01";
import Step03 from "./Step03";
import DialogContinuarProximaOrdem from "./DialogContinuarProximaOrdem";
import DialogConfirmarOrdens from "./DialogConfirmarOrdens";
import { formatarMonetárioParaNúmero } from "../../../../../../../helpers/masks";
import { enqueueSnackbar } from "notistack";
import Step02 from "./Step02";
import useMutateRascunho from "../../../../../../../lib/useMutateRascunho";
import DisplayListagemRascunhos from "../../../../../../../components/DisplayListagemRascunhos";
import { IRascunhoArquivo } from "../../../../../../../types/api/financeiro/pesquisar-rascunhos";
import { CircularProgress } from "@mui/material";
import moment from "moment";

export default function DialogGerarOc({
    open,
    handleClose,
    produtos: produtosBase,
    cotacoes,
    solicitacaoDetalhe
}: DialogGerarOcProps) {

    const queryClient = useQueryClient();
    const { handleSaveRascunho, isLoadingSalvarRascunho, handleDeleteRascunho } = useMutateRascunho("GerarOrdemCompra");

    const [activeStep, setActiveStep] = useState(0);
    const [cotacaoSelecionadaPorItem, setCotacaoSelecionadaPorItem] = useState<{ [produto_id: number]: { cotacao_id?: number; desconto?: string; acrescimo?: string; } }>({});
    const [selectedItens, setSelectedItens] = useState<number[]>([]);

    const [fornecedoresProcessados, setFornecedoresProcessados] = useState<FornecedorProcessado[]>([]);
    const [fornecedorAtualIndex, setFornecedorAtualIndex] = useState(0);
    const [descontoFretePorFornecedor, setDescontoFretePorFornecedor] = useState<{ [fornecedor: string]: string }>({});
    const [dialogContinuarOpen, setDialogContinuarOpen] = useState(false);
    const [dialogConfirmarOpen, setDialogConfirmarOpen] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);

    const methods = useForm<FormFields>({
        defaultValues: {
            fornecedor: "",
            parcelamento: {
                parcelas: [],
                valores: [],
                vencimentos: [],
                forma_primeira_parcela: 'personalizada',
                dias_corridos_primeira_parcela: '30',
                valor_primeira_parcela_customizado: null

            }
        }
    });
    const { handleSubmit, trigger } = methods;

    const { mutate: cadastrarOrdemCompra, isLoading: isLoadingCadastrar } = useMutation({
        mutationKey: ["cadastrar-ordem-compra"],
        mutationFn: async (inputs: any) => await axiosClient.post("estoque/compras/cadastrar-ordem-compra", inputs),
        onError: () => {
            enqueueSnackbar("Erro ao cadastrar ordem de compra", { variant: "error" });
        },
        onSuccess: async ({ data }) => {
            if (data.success) {
                enqueueSnackbar(`Ordem de Compra Gerada com sucesso! Nº: ${data.ordem_compra_id}`, { variant: "success" });

                const response = await axiosClient.get("/pesquisar-rascunhos", {
                    params: {
                        componente: "GerarOrdemCompra",
                        chave: "solicitacao_id",
                        tipo_valor: "int",
                        valor: solicitacaoDetalhe.id
                    }
                });

                const rascunhos = response?.data || [];

                await Promise.all(
                    rascunhos.map((rascunho: any) => handleDeleteRascunho(rascunho.id))
                );

                queryClient.invalidateQueries("detalhe-solicitacao-compra");
                handleClose();
            } else {
                enqueueSnackbar(data.error || "Erro ao gerar OC", { variant: "error" });
            }
        }
    });

    const produtos = useMemo(() => {
        return produtosBase
            .filter(produto => produto.status_cotacao === 'Cotado')
            .map(produto => {
                const cotacaoData = cotacaoSelecionadaPorItem[produto.produto_id] || {};
                let valor_unitario = 0;
                let valor_total = 0;

                let prazo_entrega = "";
                let cotacao_fornecedor = "";
                if (cotacaoData.cotacao_id) {
                    const cotacao = cotacoes.find(c => c.id === cotacaoData.cotacao_id);
                    const cotacaoItem = cotacao?.itens.find(i => i.produto_id === produto.produto_id);
                    valor_unitario = Number(cotacaoItem?.valor_unitario ?? 0);
                    valor_total = Number(cotacaoItem?.valor_total ?? 0)

                    prazo_entrega = cotacao ? `${cotacao.prazo_entrega} DIAS` : "";
                    cotacao_fornecedor = cotacao?.fornecedor ?? "";
                }
                return {
                    ...produto,
                    valor_unitario,
                    valor_desconto: cotacaoData.desconto ?? 'R$ 0,00',
                    valor_acrescimo: cotacaoData.acrescimo ?? 'R$ 0,00',
                    valor_total,
                    prazo_entrega,
                    cotacao_id: cotacaoData.cotacao_id ?? undefined,
                    cotacao_fornecedor
                };
            });
    }, [produtosBase, cotacaoSelecionadaPorItem, cotacoes]);

    const itensSelecionados = produtos.filter(p => selectedItens.includes(p.produto_id));

    const itensPorFornecedor = useMemo(() => {
        const grupos: { [fornecedor: string]: ProdutoGerarOc[] } = {};

        itensSelecionados.forEach(item => {
            if (item.cotacao_fornecedor) {
                if (!grupos[item.cotacao_fornecedor]) {
                    grupos[item.cotacao_fornecedor] = [];
                }
                grupos[item.cotacao_fornecedor].push(item);
            }
        });

        return grupos;
    }, [itensSelecionados]);

    const fornecedores = Object.keys(itensPorFornecedor);
    const fornecedorAtual = fornecedores[fornecedorAtualIndex];
    const produtosFornecedorAtual = useMemo(() =>
        itensPorFornecedor[fornecedorAtual] || [],
        [itensPorFornecedor, fornecedorAtual]
    );

    const valorFrete = useMemo(() => {
        if (produtosFornecedorAtual.length === 0) return 0;
        const cotacaoIds = produtosFornecedorAtual.map(p => p.cotacao_id).filter(Boolean) as number[];
        if (cotacaoIds.length > 0) {
            const cotacao = cotacoes.find(c => c.id === cotacaoIds[0]);
            return Number(cotacao?.valor_frete ?? 0);
        }
        return 0;
    }, [cotacoes, produtosFornecedorAtual]);

    const descontoFrete = descontoFretePorFornecedor[fornecedorAtual] || '';

    const valorProdutos = useMemo(() =>
        produtosFornecedorAtual.reduce((acc, item) =>
            acc + (Number(item.valor_unitario - formatarMonetárioParaNúmero(item.valor_desconto || '0') + formatarMonetárioParaNúmero(item.valor_acrescimo || '0')) * item.quantidade), 0
        ),
        [produtosFornecedorAtual]
    );

    const totalComFrete = useMemo(() =>
        valorProdutos + valorFrete - formatarMonetárioParaNúmero(descontoFrete || '0'),
        [valorProdutos, valorFrete, descontoFrete]
    );

    const todosSelecionadosComCotacao = useMemo(() =>
        itensSelecionados.every(p => !!p.cotacao_id),
        [itensSelecionados]
    );

    function handleSelectCotacao(produto_id: number, cotacao_id: number) {
        setCotacaoSelecionadaPorItem({
            ...cotacaoSelecionadaPorItem,
            [produto_id]: { cotacao_id, desconto: 'R$ 0,00', acrescimo: 'R$ 0,00' }
        });
    }

    function handleSelectItem(produto_id: number, selected: boolean) {
        setSelectedItens(prev =>
            selected
                ? [...prev, produto_id]
                : prev.filter(id => id !== produto_id)
        );
    }

    function handleChangeDescontoUnitario(produto_id: number, valor: string) {
        setCotacaoSelecionadaPorItem(prev => ({
            ...prev,
            [produto_id]: {
                ...prev[produto_id],
                desconto: valor
            }
        }));
    }

    function handleChangeAcrescimoUnitario(produto_id: number, valor: string) {
        setCotacaoSelecionadaPorItem(prev => ({
            ...prev,
            [produto_id]: {
                ...prev[produto_id],
                acrescimo: valor
            }
        }));
    }

    function handleChangeDescontoFrete(valor: string) {
        setDescontoFretePorFornecedor(prev => ({
            ...prev,
            [fornecedorAtual]: valor
        }));
    }

    function handleNext() {
        if (activeStep === 0) {
            if (todosSelecionadosComCotacao && fornecedores.length > 0 && itensSelecionados.length > 0) {
                setActiveStep(1);
            }
        }

        else if (activeStep === 1) {
            const descontoMaiorQueFrete = formatarMonetárioParaNúmero(descontoFrete) > Number(valorFrete);
            if (!descontoMaiorQueFrete) {
                setActiveStep(2);
            }
        }

        else if (activeStep === 2) {
            trigger().then(valid => {
                if (valid) {
                    const fornecedorData: FornecedorProcessado = {
                        fornecedor: fornecedorAtual,
                        produtos: produtosFornecedorAtual,
                        descontoFrete: descontoFrete,
                        frete: valorFrete,
                        parcelamento: methods.getValues().parcelamento
                    };

                    if (modoEdicao) {
                        setFornecedoresProcessados(prev =>
                            prev.map((f, idx) =>
                                idx === fornecedorAtualIndex ? fornecedorData : f
                            )
                        );
                        setModoEdicao(false);
                        setDialogConfirmarOpen(true);
                    } else {
                        const fornecedorJaExiste = fornecedoresProcessados.some(
                            f => f.fornecedor === fornecedorAtual
                        );

                        if (!fornecedorJaExiste) {
                            setFornecedoresProcessados(prev => [...prev, fornecedorData]);
                        }

                        if (fornecedorAtualIndex < fornecedores.length - 1) {
                            setDialogContinuarOpen(true);
                        } else {
                            setDialogConfirmarOpen(true);
                        }
                    }
                }
            });
        }
    }

    function handleBack() {
        setActiveStep((prev) => prev - 1);
    }

    function handleContinuarProximoFornecedor() {
        setDialogContinuarOpen(false);
        setFornecedorAtualIndex(prev => prev + 1);
        setActiveStep(1);
        methods.reset({
            ...methods.getValues(),
            parcelamento: {
                parcelas: [],
                valores: [],
                vencimentos: [],
                forma_primeira_parcela: 'personalizada',
                dias_corridos_primeira_parcela: '30'
            }
        });
    }

    function handleFinalizarSemContinuar() {
        setDialogContinuarOpen(false);
        setDialogConfirmarOpen(true);
    }

    function handleEditarFornecedor(index: number) {
        setDialogConfirmarOpen(false);
        setFornecedorAtualIndex(index);
        setModoEdicao(true);

        const fornecedorData = fornecedoresProcessados[index];
        setDescontoFretePorFornecedor(prev => ({
            ...prev,
            [fornecedorData.fornecedor]: fornecedorData.descontoFrete
        }));

        methods.reset({
            ...methods.getValues(),
            parcelamento: fornecedorData.parcelamento
        });

        setActiveStep(1);
    }

    function handleConfirmarTodasOrdens() {
        setDialogConfirmarOpen(false);
        finalizarTodasOrdens(fornecedoresProcessados);
    }

    function handleDownloadRascunho(data: string, arquivos: IRascunhoArquivo[]) {
        try {
            const rascunhoData = JSON.parse(data);

            if (rascunhoData.cotacao_selecionada_por_item) {
                setCotacaoSelecionadaPorItem(rascunhoData.cotacao_selecionada_por_item);
            }
            if (rascunhoData.selected_itens) {
                setSelectedItens(rascunhoData.selected_itens);
            }
            if (rascunhoData.fornecedores_processados) {
                const fornecedoresComDatas = rascunhoData.fornecedores_processados.map((f: any) => ({
                    ...f,
                    parcelamento: {
                        ...f.parcelamento,
                        parcelas: f.parcelamento.parcelas?.map((p: any) => ({
                            ...p,
                            data_vencimento: moment(p.data_vencimento)
                        })) || []
                    }
                }));
                setFornecedoresProcessados(fornecedoresComDatas);
            }
            if (rascunhoData.fornecedor_atual_index !== undefined) {
                setFornecedorAtualIndex(rascunhoData.fornecedor_atual_index);
            }
            if (rascunhoData.desconto_frete_por_fornecedor) {
                setDescontoFretePorFornecedor(rascunhoData.desconto_frete_por_fornecedor);
            }
            if (rascunhoData.parcelamento_atual) {
                const parcelamentoComDatas = {
                    ...rascunhoData.parcelamento_atual,
                    parcelas: rascunhoData.parcelamento_atual.parcelas?.map((p: any) => ({
                        ...p,
                        data_vencimento: moment(p.data_vencimento)
                    })) || []
                };

                methods.reset({
                    ...methods.getValues(),
                    parcelamento: parcelamentoComDatas
                });
            }

            enqueueSnackbar("Rascunho carregado com sucesso!", { variant: "success" });
        } catch (error) {
            enqueueSnackbar("Erro ao carregar rascunho", { variant: "error" });
        }
    }

    function mapItensForBackend(produtos: ProdutoGerarOc[]): PayloadItensGerarOc[] {
        return produtos.map(item => {
            const cotacaoId = item.cotacao_id;
            const cotacao = cotacoes.find(c => c.id === cotacaoId);
            const cotacaoItem = cotacao?.itens.find(i => i.produto_id === item.produto_id);
            return {
                tipo: 1,
                quantidade: item.quantidade,
                valor_unitario: Number(cotacaoItem?.valor_unitario ?? item.valor_unitario),
                produto_id: item.produto_id,
                valor_desconto: formatarMonetárioParaNúmero(item.valor_desconto || '0'),
                valor_acrescimo: formatarMonetárioParaNúmero(item.valor_acrescimo || '0')
            };
        });
    }

    function salvarRascunho() {
        const rascunhoData = {
            solicitacao_id: solicitacaoDetalhe.id,
            fornecedores_processados: fornecedoresProcessados,
            fornecedor_atual_index: fornecedorAtualIndex,
            cotacao_selecionada_por_item: cotacaoSelecionadaPorItem,
            selected_itens: selectedItens,
            desconto_frete_por_fornecedor: descontoFretePorFornecedor,
            parcelamento_atual: methods.getValues().parcelamento
        };

        handleSaveRascunho(rascunhoData, []);
        handleClose();
    }

    function finalizarTodasOrdens(fornecedoresData: FornecedorProcessado[]) {
        fornecedoresData.forEach(fornecedorData => {
            const cotacaoIds = fornecedorData.produtos.map(p => p.cotacao_id).filter(Boolean) as number[];
            const payload = {
                solicitacao_id: solicitacaoDetalhe.id,
                cotacoes_ids: cotacaoIds,
                itens: mapItensForBackend(fornecedorData.produtos),
                parcelas: mapParcelasForBackend(fornecedorData.parcelamento),
                forma_primeira_parcela: fornecedorData.parcelamento.forma_primeira_parcela,
            };
            cadastrarOrdemCompra(payload);
        });
    }

    function getStepContent(step: number) {
        switch (step) {
            case 0:
                return (
                    <Step01
                        produtos={produtos}
                        cotacoes={cotacoes}
                        onSelectCotacao={handleSelectCotacao}
                        selectedItens={selectedItens}
                        onSelectItem={handleSelectItem}
                    />
                );
            case 1:
                return (
                    <Step02
                        produtos={produtosFornecedorAtual}
                        valorFrete={valorFrete}
                        descontoFrete={descontoFrete}
                        totalComFrete={totalComFrete}
                        onChangeDescontoFrete={handleChangeDescontoFrete}
                        onChangeDescontoUnitario={handleChangeDescontoUnitario}
                        onChangeAcrescimoUnitario={handleChangeAcrescimoUnitario}
                        fornecedor={fornecedorAtual}
                    />
                );
            case 2:
                return (
                    <Step03
                        valorTotal={totalComFrete}
                    />
                );
            default:
                return "Passo desconhecido";
        }
    }

    const isLastStep = activeStep === steps.length - 1;

    const descontoMaiorQueFrete = useMemo(() =>
        formatarMonetárioParaNúmero(descontoFrete) > Number(valorFrete),
        [descontoFrete, valorFrete]
    );

    const disableNext = useMemo(() => {
        if (activeStep === 0) {
            return !(todosSelecionadosComCotacao && fornecedores.length > 0 && itensSelecionados.length > 0);
        } else if (activeStep === 1) {
            return descontoMaiorQueFrete || produtosFornecedorAtual.length === 0;
        }
        return false;
    }, [activeStep, todosSelecionadosComCotacao, fornecedores.length, itensSelecionados.length, descontoMaiorQueFrete, produtosFornecedorAtual.length]);

    const proximoFornecedor = fornecedorAtualIndex < fornecedores.length - 1 ? fornecedores[fornecedorAtualIndex + 1] : undefined;

    return (
        <>
            <Dialog
                open={open}
                maxWidth="lg"
                scroll="body"
                fullWidth
            >
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(() => handleNext())}>
                        <DialogTitle>
                            Gerar Ordem de Compra
                        </DialogTitle>
                        <DialogContent>

                            <DisplayListagemRascunhos
                                componente="GerarOrdemCompra"
                                onDownloadForm={handleDownloadRascunho}
                                queryFields={{
                                    chave: "solicitacao_id",
                                    tipo_valor: "int",
                                    valor: solicitacaoDetalhe.id
                                }}
                            />

                            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Box sx={{ minHeight: 400 }}>
                                {activeStep === 0 && fornecedores.length > 1 && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Foram selecionados produtos de {fornecedores.length} fornecedores diferentes. Você precisará configurar cada ordem separadamente.
                                    </Alert>
                                )}
                                {getStepContent(activeStep)}
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button
                                variant="text"
                                onClick={handleClose}
                            >
                                Cancelar
                            </Button>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Button
                                disabled={isLoadingSalvarRascunho}
                                variant="outlined"
                                onClick={() => salvarRascunho()}
                                sx={{ mr: 1 }}
                            >
                                {isLoadingSalvarRascunho && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
                                Salvar Rascunho
                            </Button>
                            <Button
                                disabled={activeStep === 0}
                                onClick={handleBack}
                                variant="outlined"
                                sx={{ mr: 1 }}
                            >
                                Anterior
                            </Button>
                            {
                                isLastStep && (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isLoadingCadastrar}
                                    >
                                        {fornecedorAtualIndex < fornecedores.length - 1 ? 'Próximo Fornecedor' : 'Finalizar'}
                                    </Button>
                                )
                            }
                            {
                                !isLastStep && (
                                    <Button
                                        variant="contained"
                                        onClick={handleNext}
                                        color="primary"
                                        disabled={disableNext}
                                    >
                                        Próximo
                                    </Button>
                                )
                            }
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>

            {
                dialogContinuarOpen && (
                    <DialogContinuarProximaOrdem
                        open={dialogContinuarOpen}
                        fornecedorAtual={fornecedorAtual}
                        proximoFornecedor={proximoFornecedor}
                        onContinuar={handleContinuarProximoFornecedor}
                        onFinalizar={handleFinalizarSemContinuar}
                        onSalvarRascunho={() => salvarRascunho()}
                    />
                )
            }

            {
                dialogConfirmarOpen && (
                    <DialogConfirmarOrdens
                        open={dialogConfirmarOpen}
                        fornecedoresProcessados={fornecedoresProcessados}
                        onConfirmar={handleConfirmarTodasOrdens}
                        onEditar={handleEditarFornecedor}
                        onCancelar={() => setDialogConfirmarOpen(false)}
                        isLoading={isLoadingCadastrar}
                    />
                )
            }
        </>
    );
}

export interface ProdutoGerarOc {
    id: number;
    produto_id: number;
    unidade_medida: string;
    observacoes?: string | null;
    nome: string;
    quantidade: number;
    status_cotacao: string;
    valor_unitario: number;
    valor_total: number;
    prazo_entrega: string;
    cotacao_id?: number;
    cotacao_fornecedor?: string;
    valor_desconto: string;
    valor_acrescimo: string;
}

interface DialogGerarOcProps {
    open: boolean;
    handleClose: () => void;
    produtos: ProdutoGerarOc[];
    cotacoes: CotacaoRealizada[];
    solicitacao_id: number;
    solicitacaoDetalhe: DetalheSolicitacaoData;
}

type FormFields = {
    fornecedor: string;
    parcelamento: {
        parcelas: any[];
        valores: number[];
        vencimentos: string[];
        forma_primeira_parcela?: 'personalizada' | 'dias_corridos';
        dias_corridos_primeira_parcela?: string;
        valor_primeira_parcela_customizado?: number | null;
    };
};

interface FornecedorProcessado {
    fornecedor: string;
    produtos: ProdutoGerarOc[];
    descontoFrete: string;
    frete: number;
    parcelamento: {
        parcelas: any[];
        valores: number[];
        vencimentos: string[];
        forma_primeira_parcela?: 'personalizada' | 'dias_corridos';
        dias_corridos_primeira_parcela?: string;
    };
}

const steps = [
    "Produtos",
    "Configuração",
    "Pagamento"
];

interface PayloadItensGerarOc {
    tipo: number;
    quantidade: number;
    valor_unitario: number;
    valor_desconto: number;
    valor_acrescimo: number;
    produto_id: number;
}

function mapParcelasForBackend(parcelamento: { valores: number[], vencimentos: string[] }) {
    return parcelamento.vencimentos.map((data_vencimento, idx) => ({
        data_vencimento,
        valor: parcelamento.valores[idx]
    }));
}